import { createSlice } from "@reduxjs/toolkit"
import {
  assignPricelistToCustomer,
  createPriceList,
  createPriceListDetail,
  createPricingCategory,
  createPricingGroup,
  deletePriceList,
  deletePriceListDetail,
  deletePricingCategory,
  deletePricingGroup,
  fetchPriceListDetails,
  fetchPriceLists,
  fetchPricingCategories,
  fetchPricingGroups,
  fetchUsersByPriceList,
  updatePriceList,
  updatePriceListDetail,
  updatePricingCategory,
  updatePricingGroup
} from "../actions/price_list/price_list_action"

const initialState = {
  categories: [],
  groups: [],
  priceLists: [],
  priceListDetails: [],
  userPriceListAssignments: {},
  loading: false,
  error: null,
  selectedPriceList: null
}

const priceSlice = createSlice({
  name: "price",
  initialState,
  reducers: {
    setSelectedPriceList: (state, action) => {
      state.selectedPriceList = action.payload
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPricingCategories.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPricingCategories.fulfilled, (state, action) => {
        state.loading = false
        state.categories = action.payload
        state.error = null
      })
      .addCase(fetchPricingCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(createPricingCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload)
      })
      .addCase(updatePricingCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(
          (category) => category.id === action.payload.id
        )
        if (index !== -1) {
          state.categories[index] = action.payload
        }
      })
      .addCase(deletePricingCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(
          (category) => category.id !== action.payload
        )
      })

      // Pricing Groups Reducers
      .addCase(fetchPricingGroups.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPricingGroups.fulfilled, (state, action) => {
        state.loading = false
        state.groups = action.payload
        state.error = null
      })
      .addCase(fetchPricingGroups.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(createPricingGroup.fulfilled, (state, action) => {
        state.groups.push(action.payload)
      })
      .addCase(updatePricingGroup.fulfilled, (state, action) => {
        const index = state.groups.findIndex(
          (group) => group.id === action.payload.id
        )
        if (index !== -1) {
          state.groups[index] = action.payload
        }
      })
      .addCase(deletePricingGroup.fulfilled, (state, action) => {
        state.groups = state.groups.filter(
          (group) => group.id !== action.payload
        )
      })

      // Price Lists Reducers
      .addCase(fetchPriceLists.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPriceLists.fulfilled, (state, action) => {
        state.loading = false
        state.priceLists = action.payload
        state.error = null
      })
      .addCase(fetchPriceLists.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(createPriceList.fulfilled, (state, action) => {
        state.priceLists.push(action.payload)
      })
      .addCase(updatePriceList.fulfilled, (state, action) => {
        const index = state.priceLists.findIndex(
          (list) => list.id === action.payload.id
        )
        if (index !== -1) {
          state.priceLists[index] = action.payload
        }
      })
      .addCase(deletePriceList.fulfilled, (state, action) => {
        state.priceLists = state.priceLists.filter(
          (list) => list.id !== action.payload
        )
      })

      // Price List Details Reducers
      .addCase(fetchPriceListDetails.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPriceListDetails.fulfilled, (state, action) => {
        state.loading = false
        state.priceListDetails = action.payload
        state.error = null
      })
      .addCase(fetchPriceListDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(createPriceListDetail.fulfilled, (state, action) => {
        state.priceListDetails.push(action.payload)
      })
      .addCase(updatePriceListDetail.fulfilled, (state, action) => {
        const index = state.priceListDetails.findIndex(
          (detail) => detail.id === action.payload.id
        )
        if (index !== -1) {
          state.priceListDetails[index] = action.payload
        }
      })
      .addCase(deletePriceListDetail.fulfilled, (state, action) => {
        state.priceListDetails = state.priceListDetails.filter(
          (detail) => detail.id !== action.payload
        )
      })

      .addCase(assignPricelistToCustomer.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(assignPricelistToCustomer.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.error = null

        const { userId, pricelistId } = action.meta.arg

        if (!state.userPriceListAssignments) {
          state.userPriceListAssignments = {}
        }

        state.userPriceListAssignments[userId] = pricelistId
      })
      .addCase(assignPricelistToCustomer.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.success = false
      })

      .addCase(fetchUsersByPriceList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsersByPriceList.fulfilled, (state, action) => {
        state.loading = false
        state.usersByPriceList = action.payload
        state.error = null
      })
      .addCase(fetchUsersByPriceList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { setSelectedPriceList, clearError } = priceSlice.actions

export default priceSlice.reducer
